import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { useStepUpStatus } from '@/hooks/useStepUpStatus'
import { stepUpStartUrl, type StepUpAction } from '@/lib/step-up'

/**
 * KAN-857 U8 — wraps a destructive dialog's primary button (design §5.7).
 *
 * - Not required (the dark default, or the action not enabled), or an active
 *   grant: renders the button unchanged.
 * - Required and no grant: the primary button becomes "Confirm it's you",
 *   a top-level navigation to the step-up start. The admin confirms BEFORE
 *   typing a reason, so nothing is lost.
 * - The gate is failing closed server-side: a disabled button that says so.
 */
export function StepUpGate({
  action,
  open,
  children,
  navigate = (url: string) => window.location.assign(url),
}: {
  action: StepUpAction
  open: boolean
  children: ReactNode
  navigate?: (url: string) => void
}) {
  const { status } = useStepUpStatus(action, open)
  if (status.unavailable) {
    return (
      <Button disabled title="Confirmation is temporarily unavailable">
        Temporarily unavailable
      </Button>
    )
  }
  if (!status.required || status.active) return <>{children}</>
  return (
    <Button onClick={() => navigate(stepUpStartUrl(action, window.location.pathname))}>
      Confirm it&apos;s you
    </Button>
  )
}

/** One line above the form while a confirmation is needed. */
export function StepUpNotice({ action, open }: { action: StepUpAction; open: boolean }) {
  const { status } = useStepUpStatus(action, open)
  if (!status.required || status.active || status.unavailable) return null
  return (
    <p role="note" className="text-sm text-muted-foreground">
      For your security, confirm it&apos;s you before this action. You will be asked to sign in again.
    </p>
  )
}
